import { type Dispatch, type MutableRefObject, type SetStateAction, useCallback } from 'react';
import { calendarService } from '~/src/services/calendarService';
import type { AccountConnections, CalendarEvent } from '~/src/types/api';
import { withMicrosoftProvider } from '../helpers/calendarEventHelpers';

type MergeEventsWithDeltaFn = (
  previousEvents: CalendarEvent[],
  deltaEvents: CalendarEvent[],
  provider: 'google' | 'microsoft',
) => CalendarEvent[];

type UseIncrementalCalendarSyncParams = {
  accountConnections?: AccountConnections;
  activeTab: string;
  googleSyncTokenRef: MutableRefObject<Record<string, string | undefined>>;
  microsoftDeltaLinkRef: MutableRefObject<Record<string, string | undefined>>;
  setGoogleEvents: Dispatch<SetStateAction<CalendarEvent[]>>;
  setMicrosoftEvents: Dispatch<SetStateAction<CalendarEvent[]>>;
  mergeEventsWithDelta: MergeEventsWithDeltaFn;
  reportCalendarAuthError?: (provider: 'google' | 'microsoft', message?: string) => void;
};

export const useIncrementalCalendarSync = ({
  accountConnections,
  activeTab,
  googleSyncTokenRef,
  microsoftDeltaLinkRef,
  setGoogleEvents,
  setMicrosoftEvents,
  mergeEventsWithDelta,
  reportCalendarAuthError,
}: UseIncrementalCalendarSyncParams) => {
  const syncGoogleIncrementally = useCallback(async () => {
    if (!accountConnections?.google?.connected) {
      return;
    }

    const currentSyncToken = googleSyncTokenRef.current[activeTab];
    if (!currentSyncToken) {
      // Keep first-page behavior (5 records) when sync token is unavailable.
      const page = await calendarService.fetchGoogleCalendarEventsList(
        accountConnections.google,
        activeTab,
      );
      reportCalendarAuthError?.('google', page.authError);
      const mappedEvents = ((page.events ?? []) as CalendarEvent[]).map((event) => ({
        ...event,
        provider: 'google',
      }));
      setGoogleEvents((prev) =>
        prev.length > 0 ? mergeEventsWithDelta(prev, mappedEvents, 'google') : mappedEvents,
      );
      if (page.nextSyncToken) {
        googleSyncTokenRef.current[activeTab] = page.nextSyncToken;
      }
      return;
    }

    let nextPageToken: string | undefined;
    let nextSyncToken: string | undefined = currentSyncToken;
    const deltaEvents: CalendarEvent[] = [];

    do {
      const page = await calendarService.fetchGoogleCalendarEventsDelta(
        accountConnections.google,
        currentSyncToken,
        nextPageToken,
      );
      reportCalendarAuthError?.('google', page.authError);
      const mappedEvents = ((page.events ?? []) as CalendarEvent[]).map((event) => ({
        ...event,
        provider: 'google',
      }));
      deltaEvents.push(...mappedEvents);
      nextPageToken = page.nextPageToken;
      if (page.nextSyncToken) {
        nextSyncToken = page.nextSyncToken;
      }
    } while (nextPageToken);

    if (nextSyncToken) {
      googleSyncTokenRef.current[activeTab] = nextSyncToken;
    }

    if (deltaEvents.length > 0) {
      setGoogleEvents((prev) => mergeEventsWithDelta(prev, deltaEvents, 'google'));
    }
  }, [
    accountConnections,
    activeTab,
    googleSyncTokenRef,
    mergeEventsWithDelta,
    reportCalendarAuthError,
    setGoogleEvents,
  ]);

  const syncMicrosoftIncrementally = useCallback(async () => {
    if (!accountConnections?.microsoft?.connected) {
      return;
    }

    const hadDeltaCursor = Boolean(microsoftDeltaLinkRef.current[activeTab]);
    if (!hadDeltaCursor) {
      // Keep first-page behavior (5 records) until delta cursor is available.
      const page = await calendarService.fetchMicrosoftCalendarEventsList(
        accountConnections.microsoft,
        activeTab,
      );
      reportCalendarAuthError?.('microsoft', page.authError);
      const mappedEvents = ((page.events ?? []) as CalendarEvent[]).map((event) =>
        withMicrosoftProvider(event),
      );
      setMicrosoftEvents((prev) =>
        prev.length > 0 ? mergeEventsWithDelta(prev, mappedEvents, 'microsoft') : mappedEvents,
      );
      if (page.deltaLink) {
        microsoftDeltaLinkRef.current[activeTab] = page.deltaLink;
      }
      return;
    }

    let nextLink = microsoftDeltaLinkRef.current[activeTab];
    let latestDeltaLink = nextLink;
    const deltaEvents: CalendarEvent[] = [];

    do {
      const page = await calendarService.fetchMicrosoftCalendarEventsDelta(
        accountConnections.microsoft,
        activeTab,
        nextLink,
      );
      reportCalendarAuthError?.('microsoft', page.authError);
      const mappedEvents = ((page.events ?? []) as CalendarEvent[]).map((event) =>
        withMicrosoftProvider(event),
      );
      deltaEvents.push(...mappedEvents);
      nextLink = page.nextLink;
      if (page.deltaLink) {
        latestDeltaLink = page.deltaLink;
      }
    } while (nextLink);

    if (latestDeltaLink) {
      microsoftDeltaLinkRef.current[activeTab] = latestDeltaLink;
    }

    if (deltaEvents.length > 0) {
      setMicrosoftEvents((prev) => mergeEventsWithDelta(prev, deltaEvents, 'microsoft'));
    }
  }, [
    accountConnections,
    activeTab,
    mergeEventsWithDelta,
    microsoftDeltaLinkRef,
    reportCalendarAuthError,
    setMicrosoftEvents,
  ]);

  const syncCalendarIncrementally = useCallback(async () => {
    await Promise.all([syncGoogleIncrementally(), syncMicrosoftIncrementally()]);
  }, [syncGoogleIncrementally, syncMicrosoftIncrementally]);

  return {
    syncCalendarIncrementally,
  };
};
